import { InvalidGltfManifestError } from '@/plugins/mesh/errors'
import { createBufferLocations } from '@/plugins/mesh/loaders/gltfLowMemory/DefaultGltfManifestReader'
import { validateGltfManifest } from '@/plugins/mesh/loaders/gltfLowMemory/manifestValidation'
import type { GltfPreflightReader } from '@/plugins/mesh/loaders/gltfLowMemory/preflightRead'
import type {
  GlbContainerInfo,
  GltfManifest,
  GltfManifestDocument,
} from '@/plugins/mesh/loaders/gltfLowMemory/types'
import { describeMeshSource, resolveMeshBaseUrl } from '@/plugins/mesh/loaders/loaderUtils'
import type { MeshLoadRequest } from '@/plugins/mesh/types'

/** 使用切片或有限 HTTP 响应解析 glTF 元数据。 */
export async function readGltfPreflightManifest(
  request: MeshLoadRequest,
  format: 'gltf' | 'glb',
  reader: GltfPreflightReader
): Promise<GltfManifestDocument> {
  const source = describeMeshSource(request)
  const input = request.data ?? request.url
  if (input === undefined) throw new InvalidGltfManifestError(source, '缺少来源')
  const baseUrl = resolveMeshBaseUrl(request)
  let manifest: GltfManifest
  let glb: GlbContainerInfo | undefined
  if (format === 'gltf') {
    const text =
      typeof request.data === 'string'
        ? request.data
        : new TextDecoder().decode(await reader.readAll(input))
    const maxBytes = request.gltf?.budget?.maxPreflightBytes ?? Infinity
    if (
      typeof request.data === 'string' &&
      Number.isFinite(maxBytes) &&
      new TextEncoder().encode(text).byteLength > maxBytes
    )
      throw new InvalidGltfManifestError(source, 'glTF JSON 超过调用方预检额度')
    manifest = validateGltfManifest(JSON.parse(text), source)
  } else {
    if (typeof request.data === 'string') {
      throw new InvalidGltfManifestError(source, 'GLB 内存输入不能是字符串')
    }
    const header = await reader.read(input, 0, 20)
    const view = new DataView(header.buffer, header.byteOffset, header.byteLength)
    const totalLength = view.getUint32(8, true)
    const jsonLength = view.getUint32(12, true)
    if (
      view.getUint32(0, true) !== 0x46546c67 ||
      view.getUint32(4, true) !== 2 ||
      view.getUint32(16, true) !== 0x4e4f534a ||
      totalLength % 4 !== 0 ||
      jsonLength === 0 ||
      jsonLength % 4 !== 0 ||
      jsonLength + 20 > totalLength
    ) {
      throw new InvalidGltfManifestError(source, 'GLB header 或 JSON chunk 无效')
    }
    const knownLength =
      input instanceof Blob ? input.size : typeof input !== 'string' ? input.byteLength : undefined
    if (knownLength !== undefined && knownLength !== totalLength) {
      throw new InvalidGltfManifestError(source, 'GLB 声明长度与本地数据不一致')
    }
    const json = await reader.read(input, 20, jsonLength)
    manifest = validateGltfManifest(
      JSON.parse(new TextDecoder().decode(json).replace(/[\s\0]+$/u, '')),
      source
    )
    glb = {
      version: 2,
      totalLength,
      jsonChunk: { type: 0x4e4f534a, byteOffset: 20, byteLength: jsonLength },
      unknownChunks: [],
    }
  }
  return {
    manifest,
    format,
    source,
    baseUrl,
    glb,
    // 粗略预检不读取 BIN 块头，不构造未经校验的二进制位置。
    bufferLocations:
      format === 'glb' ? [] : createBufferLocations(manifest, format, baseUrl, source),
    metadataOnly: format === 'glb',
    sourceData: request.data,
  }
}
